import { useMemo } from "react"
import { Client, ConditionGroup, HouseholdPerson, NextPageParams, PaginationMetadata } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { ClientPeopleResponse, fetchClientPeople } from "./api"

export function useInfiniteClientPeople(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort, viewType } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )
  return useInfiniteQuery<
    ClientPeopleResponse,
    Error,
    { clients: (Client | HouseholdPerson)[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["clients", "infinite", viewType, searchTerm, computedFilterGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const content = await getContext()
      if (!content) throw new Error("No content found")

      return fetchClientPeople(content, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      clients: data.pages.flatMap((page) => page.clients),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}
